import { Box } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { CommandDropdownContext, LevelContext, pathsEqual, pathToKey } from "./context";
import {
    BaseCommandDropdownProps,
    CommandDropdownContextValue,
    NavigationItem,
    Path,
    PathSelection,
    SelectionEvent,
} from "./types";

// Import all compound components
import { Content } from "./parts/Content";
import { Empty } from "./parts/Empty";
import { Group } from "./parts/Group";
import { LazyLevel } from "./parts/LazyLevel";
import { Level } from "./parts/Level";
import { Loading } from "./parts/Loading";
import { Modal } from "./parts/Modal";
import { Search } from "./parts/Search";
import { Separator } from "./parts/Separator";
import { Trigger } from "./parts/Trigger";

// Utility: ancestry checks for hierarchical path selections.
const isPathAncestor = (ancestor: Path, path: Path): boolean => {
    if (ancestor.length >= path.length) return false;
    return ancestor.every((key, index) => key === path[index]);
};

// ============ Root Component ============
// BaseCommandDropdownRoot
// - Owns all shared state and registries (labels, ids, items) for the compound pieces.
// - Exposes context APIs for navigation, selection, and keyboard actions.
// - In controlled mode, derives selections from the provided value prop.
function BaseCommandDropdownRoot({
    children,
    onSelect,
    value,
    multiple = false,
    filterMode = "none",
    searchValue: controlledSearchValue,
    onSearchChange,
    enableKeyboard = true,
    isOpen: controlledIsOpen,
    onOpenChange,
    className,
}: BaseCommandDropdownProps) {
    // Internal state for uncontrolled mode
    const [internalSearchValue, setInternalSearchValue] = useState("");
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [activePath, setActivePath] = useState<Path>([]);
    const [itemCount, setItemCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Drill-down navigation state
    const [navigationStack, setNavigationStack] = useState<NavigationItem[]>([]);

    // Keyboard item actions map (index -> action)
    const itemActionsRef = useRef<Map<number, { select?: () => void; drill?: () => void }>>(
        new Map()
    );
    const closeResetTimeoutRef = useRef<number | null>(null);

    // Path label registry (path -> label), path registry (id -> path), item registry (path -> item).
    // These are populated by Level/RegistryHydrator so selection/labels work across the hierarchy.
    const labelRegistryRef = useRef<Map<string, string>>(new Map());
    const idPathRegistryRef = useRef<Map<string | number, Path>>(new Map());
    const itemRegistryRef = useRef<Map<string, unknown>>(new Map());
    const [registryVersion, setRegistryVersion] = useState(0);

    // Use controlled or internal state
    const searchValue =
        controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue;
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

    // Convert value prop to selections (controlled mode), supports ids if unique.
    const buildLabelsFromPath = useCallback((path: Path): string[] => {
        return path.map((key, index) => {
            const prefix = path.slice(0, index + 1);
            return labelRegistryRef.current.get(pathToKey(prefix)) ?? String(key);
        });
    }, []);

    const controlledSelections = useMemo(() => {
        if (value === null || value === undefined) return [];

        // Single id value
        if (typeof value === "string" || typeof value === "number") {
            const path = idPathRegistryRef.current.get(value);
            return path
                ? [
                      {
                          path,
                          labels: buildLabelsFromPath(path),
                          item: itemRegistryRef.current.get(pathToKey(path)),
                      },
                  ]
                : [];
        }

        if (!Array.isArray(value) || value.length === 0) {
            return [];
        }

        // Path[] (multiple paths)
        if (Array.isArray(value[0])) {
            return (value as Path[]).map((path) => ({
                path,
                labels: buildLabelsFromPath(path),
                item: itemRegistryRef.current.get(pathToKey(path)),
            }));
        }

        // Array of ids in multiple mode
        if (multiple) {
            return (value as Array<string | number>)
                .map((id) => idPathRegistryRef.current.get(id))
                .filter((path): path is Path => !!path)
                .map((path) => ({
                    path,
                    labels: buildLabelsFromPath(path),
                    item: itemRegistryRef.current.get(pathToKey(path)),
                }));
        }

        // Single path in single mode
        const path = value as Path;
        return [
            {
                path,
                labels: buildLabelsFromPath(path),
                item: itemRegistryRef.current.get(pathToKey(path)),
            },
        ];
    }, [value, multiple, registryVersion, buildLabelsFromPath]);

    // Selection is controlled only (value prop drives it).
    const selections = controlledSelections;

    const setSearchValue = useCallback(
        (newValue: string) => {
            if (onSearchChange) {
                onSearchChange(newValue);
            } else {
                setInternalSearchValue(newValue);
            }
        },
        [onSearchChange]
    );

    // Reset navigation when closing modal to avoid stale drill-down state.
    const resetNavigation = useCallback(() => {
        setNavigationStack([]);
    }, []);

    const setIsOpen = useCallback(
        (open: boolean) => {
            if (closeResetTimeoutRef.current !== null) {
                window.clearTimeout(closeResetTimeoutRef.current);
                closeResetTimeoutRef.current = null;
            }

            if (onOpenChange) {
                onOpenChange(open);
            } else {
                setInternalIsOpen(open);
            }
            // Reset search and navigation when closing
            if (!open) {
                closeResetTimeoutRef.current = window.setTimeout(() => {
                    setSearchValue("");
                    resetNavigation();
                    closeResetTimeoutRef.current = null;
                }, 300);
            }
        },
        [onOpenChange, setSearchValue, resetNavigation]
    );

    // Navigation methods for drill-down stacks.
    const pushNavigation = useCallback(
        (key: string | number, label: string, item: unknown) => {
            setNavigationStack((prev) => [...prev, { key, label, item }]);
            // Clear search when navigating to a new level
            setSearchValue("");
        },
        [setSearchValue]
    );

    const popNavigation = useCallback(() => {
        setNavigationStack((prev) => prev.slice(0, -1));
        // Clear search when going back
        setSearchValue("");
    }, [setSearchValue]);

    const navigateToIndex = useCallback(
        (index: number) => {
            setNavigationStack((prev) => prev.slice(0, index));
            setSearchValue("");
        },
        [setSearchValue]
    );

    // Current navigation item (top of stack).
    const currentNavigation =
        navigationStack.length > 0 ? navigationStack[navigationStack.length - 1] : null;

    // Initialize navigation to show the level containing the selected item (single mode only).
    const initializeNavigationForSelection = useCallback((): boolean => {
        if (multiple || selections.length === 0) return false;

        const selectedPath = selections[0].path;
        if (selectedPath.length <= 1) return false; // Already at root level, no need to navigate

        // Build navigation stack for all keys except the last one
        // This navigates to the level containing the selected item
        const navStack: NavigationItem[] = selectedPath.slice(0, -1).map((key, index) => {
            const pathToHere = selectedPath.slice(0, index + 1);
            const label = labelRegistryRef.current.get(pathToKey(pathToHere)) ?? String(key);
            return {
                key,
                label,
                item: null,
            };
        });

        setNavigationStack(navStack);
        return true;
    }, [multiple, selections]);

    // Registry operations - keep label/path/item maps in sync with latest data.
    const registerLabel = useCallback((path: Path, label: string) => {
        const key = pathToKey(path);
        const existing = labelRegistryRef.current.get(key);
        if (existing !== label) {
            labelRegistryRef.current.set(key, label);
            setRegistryVersion((prev) => prev + 1);
        }
    }, []);

    const getLabelForPath = useCallback((path: Path) => {
        return labelRegistryRef.current.get(pathToKey(path));
    }, []);

    const updateNavigationLabel = useCallback((path: Path, label: string) => {
        if (path.length === 0) return;
        const depthIndex = path.length - 1;
        const key = path[depthIndex];
        setNavigationStack((prev) => {
            if (depthIndex >= prev.length) return prev;
            const nav = prev[depthIndex];
            if (nav.key !== key || nav.label === label) {
                return prev;
            }
            const next = [...prev];
            next[depthIndex] = { ...nav, label };
            return next;
        });
    }, []);

    const registerPathForId = useCallback((id: string | number, path: Path) => {
        const existing = idPathRegistryRef.current.get(id);
        if (!existing || !pathsEqual(existing, path)) {
            idPathRegistryRef.current.set(id, path);
            setRegistryVersion((prev) => prev + 1);
        }
    }, []);

    const getPathForId = useCallback((id: string | number) => {
        return idPathRegistryRef.current.get(id);
    }, []);

    const registerItemForPath = useCallback((path: Path, item: unknown) => {
        const key = pathToKey(path);
        const existing = itemRegistryRef.current.get(key);
        if (existing !== item) {
            itemRegistryRef.current.set(key, item);
            setRegistryVersion((prev) => prev + 1);
        }
    }, []);

    const getItemForPath = useCallback((path: Path) => {
        return itemRegistryRef.current.get(pathToKey(path));
    }, []);

    // Handle item selection - now path-based.
    // In multiple mode we de-dupe parent/child selections by removing ancestors/descendants.
    const onItemSelect = useCallback(
        (path: Path, label: string, item?: unknown) => {
            // Store label for this path (leaf or selectable node)
            registerLabel(path, label);
            // Build labels from navigation stack + current item label
            const labels = [...navigationStack.map((nav) => nav.label), label];

            const currentSelection: PathSelection = { path, labels, item };

            // Check if this path is already selected (for toggle in multiple mode)
            const existingIndex = selections.findIndex((s) => pathsEqual(s.path, path));
            const isAlreadySelected = existingIndex !== -1;

            let newSelections: PathSelection[];
            let action: "select" | "deselect";

            if (multiple) {
                if (isAlreadySelected) {
                    // Deselect: remove from selections
                    newSelections = selections.filter((_, i) => i !== existingIndex);
                    action = "deselect";
                } else {
                    // Select: replace any ancestor/descendant selections to avoid hierarchy duplication
                    const filteredSelections = selections.filter((selection) => {
                        if (pathsEqual(selection.path, path)) return false;
                        if (isPathAncestor(selection.path, path)) return false;
                        if (isPathAncestor(path, selection.path)) return false;
                        return true;
                    });

                    newSelections = [...filteredSelections, currentSelection];
                    action = "select";
                }
            } else if (isAlreadySelected) {
                // Single selection mode: deselect when clicking the selected item
                newSelections = [];
                action = "deselect";
            } else {
                // Single selection mode: select
                newSelections = [currentSelection];
                action = "select";
            }

            // Build and emit SelectionEvent for consumers to sync controlled value.
            const event: SelectionEvent = {
                current: action === "deselect" ? undefined : currentSelection,
                all: newSelections,
                action,
            };

            onSelect?.(event);

            // Close modal after selection (unless multiple mode)
            if (!multiple) {
                setIsOpen(false);
            }
        },
        [onSelect, multiple, setIsOpen, navigationStack, selections, registerLabel]
    );

    // Remove selection from outside (chips/trigger).
    const removeSelection = useCallback(
        (path: Path) => {
            const existingIndex = selections.findIndex((s) => pathsEqual(s.path, path));
            if (existingIndex === -1) return;

            const newSelections = selections.filter((_, i) => i !== existingIndex);

            const event: SelectionEvent = {
                current: undefined,
                all: newSelections,
                action: "deselect",
            };

            onSelect?.(event);
        },
        [selections, onSelect]
    );

    // Visible item count (used by Empty and keyboard bounds).
    const registerItems = useCallback((count: number) => {
        setItemCount(count);
        // Reset active index when items change (stay in bounds)
        setActiveIndex((prev) => Math.min(prev, Math.max(0, count - 1)));
    }, []);

    // Keyboard item action registration (select vs drill are stored per index).
    const registerItemAction = useCallback(
        (index: number, action: () => void, mode: "select" | "drill" = "select") => {
            const existing = itemActionsRef.current.get(index) ?? {};
            itemActionsRef.current.set(index, { ...existing, [mode]: action });
        },
        []
    );

    const clearItemActions = useCallback(() => {
        itemActionsRef.current.clear();
    }, []);

    // Trigger the active item action based on keyboard mode.
    const triggerActiveItem = useCallback(
        (mode: "select" | "drill" = "select") => {
            const action = itemActionsRef.current.get(activeIndex)?.[mode];
            if (action) {
                action();
            }
        },
        [activeIndex]
    );

    // Context value for compound components.
    const contextValue = useMemo<CommandDropdownContextValue>(
        () => ({
            searchValue,
            setSearchValue,
            filterMode,
            isOpen,
            setIsOpen,
            activePath,
            setActivePath,
            activeIndex,
            setActiveIndex,
            enableKeyboard,
            // Drill-down navigation
            navigationStack,
            pushNavigation,
            popNavigation,
            resetNavigation,
            navigateToIndex,
            currentNavigation,
            initializeNavigationForSelection,
            registerLabel,
            getLabelForPath,
            updateNavigationLabel,
            registerPathForId,
            getPathForId,
            registerItemForPath,
            getItemForPath,
            // Selection - now path-based
            selections,
            multiple,
            onItemSelect,
            removeSelection,
            itemCount,
            registerItems,
            isLoading,
            setIsLoading,
            // Keyboard item actions
            registerItemAction,
            clearItemActions,
            triggerActiveItem,
        }),
        [
            searchValue,
            setSearchValue,
            filterMode,
            isOpen,
            setIsOpen,
            activePath,
            activeIndex,
            enableKeyboard,
            navigationStack,
            pushNavigation,
            popNavigation,
            resetNavigation,
            navigateToIndex,
            currentNavigation,
            initializeNavigationForSelection,
            registerLabel,
            getLabelForPath,
            updateNavigationLabel,
            registerPathForId,
            getPathForId,
            registerItemForPath,
            getItemForPath,
            selections,
            multiple,
            onItemSelect,
            removeSelection,
            itemCount,
            registerItems,
            isLoading,
            registerItemAction,
            clearItemActions,
            triggerActiveItem,
        ]
    );

    // Level context for root level.
    const levelContextValue = useMemo(
        () => ({
            depth: 0,
            parentPath: [] as Path,
        }),
        []
    );

    return (
        <CommandDropdownContext.Provider value={contextValue}>
            <LevelContext.Provider value={levelContextValue}>
                <Box className={className}>{children}</Box>
            </LevelContext.Provider>
        </CommandDropdownContext.Provider>
    );
}

// ============ Compound Component Assembly ============
export const BaseCommandDropdown = Object.assign(BaseCommandDropdownRoot, {
    Search,
    Content,
    Level,
    LazyLevel,
    Group,
    Empty,
    Loading,
    Separator,
    Trigger,
    Modal,
});

// Re-export types for convenience
// Re-export hooks
export { useCommandDropdown, useLevelContext } from "./context";
export type {
    BaseCommandDropdownProps,
    CommandDropdownContextValue,
    ContentProps,
    EmptyProps,
    FilterMode,
    GroupProps,
    ItemRenderProps,
    LazyLevelProps,
    LevelContextValue,
    LevelProps,
    LoadingProps,
    ModalProps,
    NavigationItem,
    Path,
    PathSelection,
    SearchProps,
    SearchRenderProps,
    SelectionEvent,
    SeparatorProps,
    TriggerProps,
} from "./types";

export default BaseCommandDropdown;
